import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";
import type { DateFormat } from "@/services/api";

interface DateFormatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sampleDates: string[];
  onSelect: (format: DateFormat) => void;
}

/**
 * Formats a sample date string into a human-readable interpretation
 * given a specific date format assumption.
 */
function interpretDate(sample: string, format: DateFormat): string {
  // Extract just the date portion (before any time component)
  const dateOnly = sample.trim().split(/[\sT]/)[0];
  if (!dateOnly) return sample;

  const parts = dateOnly.split(/[-\/]/);
  if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return sample;

  const first = parseInt(parts[0]);
  const second = parseInt(parts[1]);
  const year = parseInt(parts[2]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  if (format === "european") {
    const monthName = months[second - 1];
    return monthName ? `${monthName} ${first}, ${year}` : sample;
  } else {
    const monthName = months[first - 1];
    return monthName ? `${monthName} ${second}, ${year}` : sample;
  }
}

export function DateFormatDialog({
  open,
  onOpenChange,
  sampleDates,
  onSelect,
}: DateFormatDialogProps) {
  const handleSelect = (format: DateFormat) => {
    onSelect(format);
    onOpenChange(false);
  };

  const displaySample = sampleDates[0] || "05-03-2025";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className={cn(
                  "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg",
                  "bg-dark-900 backdrop-blur-xl border border-dark-700 rounded-xl shadow-2xl",
                )}
                initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                transition={{ type: "spring", duration: 0.3 }}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-brand-blue/20 text-brand-blue">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <DialogPrimitive.Title className="text-lg font-semibold mb-2">
                        Which date format is your data in?
                      </DialogPrimitive.Title>
                      <DialogPrimitive.Description className="text-sm text-muted-foreground">
                        Your file contains dates like <span className="font-mono text-white">{displaySample}</span> that
                        could be interpreted as either format. Please select the correct one.
                      </DialogPrimitive.Description>
                    </div>
                    <DialogPrimitive.Close asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </DialogPrimitive.Close>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <button
                      onClick={() => handleSelect("european")}
                      className={cn(
                        "flex flex-col items-start gap-1 p-4 rounded-lg border transition-colors",
                        "border-dark-700 bg-dark-800/50 hover:border-brand-blue hover:bg-brand-blue/10",
                      )}
                    >
                      <span className="text-sm font-medium">DD-MM-YYYY</span>
                      <span className="text-xs text-muted-foreground">European</span>
                      <span className="text-xs text-white mt-1">
                        {displaySample} = {interpretDate(displaySample, "european")}
                      </span>
                    </button>
                    <button
                      onClick={() => handleSelect("us")}
                      className={cn(
                        "flex flex-col items-start gap-1 p-4 rounded-lg border transition-colors",
                        "border-dark-700 bg-dark-800/50 hover:border-brand-blue hover:bg-brand-blue/10",
                      )}
                    >
                      <span className="text-sm font-medium">MM-DD-YYYY</span>
                      <span className="text-xs text-muted-foreground">US</span>
                      <span className="text-xs text-white mt-1">
                        {displaySample} = {interpretDate(displaySample, "us")}
                      </span>
                    </button>
                  </div>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
